import '@material/web/icon/icon.js'
import '@operato/property-editor/ox-properties-dynamic-view.js'
import '@operato/markdown'

import { css, html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import * as XLSX from 'xlsx'

import { ScrollbarStyles } from '@operato/styles'
import { i18next } from '@operato/i18n'

const ICON_EXCEL_EXPORT = new URL('../../icons/icon-excel-export.png', import.meta.url).href

@customElement('ox-board-component-info')
export class BoardComponentInfo extends LitElement {
  static styles = [
    ScrollbarStyles,
    css`
      :host {
        background-color: var(--md-sys-color-surface);
        font-size: 12px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        border: 2px solid var(--md-sys-color-on-primary-container);
        border-radius: 3px;

        --md-icon-size: 12px;
      }

      [header] {
        background-color: var(--md-sys-color-on-primary-container);
        user-select: none;
        align-items: center;
        color: #eee;
        padding: 0 2px;

        display: flex;
      }

      [header] md-icon {
        margin-left: auto;
        align-self: center;
      }

      [content] {
        overflow: auto;
      }

      table {
        border-collapse: collapse;
        background-color: var(--md-sys-color-surface);
        width: 100%;
        margin: auto;
        table-layout: fixed;
      }

      tr {
        border-bottom: var(--border-dim-color);
      }

      td {
        padding: var(--spacing-small);
        border-right: var(--border-dim-color);
        font-size: 0.8em;
      }

      [subTh] {
        text-align: center !important;
        font-weight: bold;
        width: 40px;
        text-transform: capitalize;
        background-color: rgba(0, 0, 0, 0.05);
      }

      span.export {
        position: absolute;
        width: 20px;
        height: 20px;
        top: 4px;
        right: 4px;
        background: var(--url-icon-excel-export) no-repeat;
      }

      a {
        text-decoration: none;
      }
    `
  ]

  @property({ type: Object }) component?: any

  private dragEndHandler = this.onDragEnd.bind(this) as EventListener
  private dragMoveHandler = this.onDragMove.bind(this) as EventListener

  connectedCallback(): void {
    super.connectedCallback()

    this.style.setProperty('--url-icon-excel-export', `url(${ICON_EXCEL_EXPORT})`)

    document.addEventListener('mouseup', this.dragEndHandler)
    document.addEventListener('mousemove', this.dragMoveHandler)
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()

    document.removeEventListener('mouseup', this.dragEndHandler)
    document.removeEventListener('mousemove', this.dragMoveHandler)
  }

  render() {
    const { state, data, value } = this.component || {}
    const { id, type } = state || {}

    return html`
      <div header @mousedown=${this.onDragStart.bind(this)} draggable="false">
        Component Inspection<md-icon @click=${(e: MouseEvent) => this.dispatchEvent(new CustomEvent('close'))}
          >close</md-icon
        >
      </div>
      <div content @wheel=${(e: WheelEvent) => this.onWheel(e)}>
        <table>
          <tr>
            <td subTh>${i18next.t('label.id')}</td>
            <td>
              <a
                @click=${(e: MouseEvent) => {
                  this.component.trigger('reactionreset')
                  this.component.trigger('reaction')
                  return false
                }}
                >${id}</a
              >
            </td>
          </tr>
          <tr>
            <td subTh>${i18next.t('label.type')}</td>
            <td>${type}</td>
          </tr>
          <tr>
            <td subTh>${i18next.t('label.value')}</td>
            <td>${value === data ? 'equal to data' : html`<pre>${JSON.stringify(value, null, 2)}</pre>`}</td>
          </tr>

          <tr>
            <td colspan="2" style="position: relative;">
              <pre>${JSON.stringify(data, null, 2)}</pre>
              ${data && Array.isArray(data)
                ? html`<span class="export" @click=${() => this.exportData()}></span>`
                : nothing}
            </td>
          </tr>
        </table>
      </div>
    `
  }

  private dragStart?: { x: number; y: number }

  onWheel(e: WheelEvent) {
    const scrollableDiv = e.currentTarget! as HTMLElement
    const contentDiv = scrollableDiv.querySelector('table')!

    // 컨텐츠의 높이와 div의 높이를 비교하여 수평 스크롤을 동작시키거나 수직 스크롤을 동작시킴
    if (contentDiv.offsetHeight > scrollableDiv.offsetHeight) {
      // 수직 스크롤 가능한 경우
      scrollableDiv.scrollTop += e.deltaY
    } else {
      // 수직 스크롤이 없는 경우
      scrollableDiv.scrollLeft += e.deltaY
    }
  }

  onDragStart(e: MouseEvent) {
    e.stopPropagation()

    if (e.button == 0) {
      this.dragStart = {
        x: e.clientX,
        y: e.clientY
      }
    }

    return false
  }

  onDragMove(e: Event) {
    if (!this.dragStart) {
      return false
    }

    e.stopPropagation()
    e.preventDefault()

    var x = (e as MouseEvent).clientX,
      y = (e as MouseEvent).clientY

    const dragStart = { x, y }

    x -= this.dragStart.x
    y -= this.dragStart.y

    this.dragStart = dragStart

    this.style.left =
      Math.min(this.parentElement!.offsetWidth - this.offsetWidth, Math.max(0, this.offsetLeft + x)) + 'px'
    this.style.top =
      Math.min(this.parentElement!.offsetHeight - this.offsetHeight, Math.max(0, this.offsetTop + y)) + 'px'

    return false
  }

  onDragEnd(e: Event) {
    if (this.dragStart) {
      e.stopPropagation()
      e.preventDefault()

      delete this.dragStart
    }
  }

  async exportData() {
    try {
      const { id = 'board-component', data } = this.component || {}
      const filename = `${id}.xlsx`

      const worksheet = XLSX.utils.json_to_sheet(data)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
      XLSX.writeFile(workbook, filename)
    } catch (e) {
      console.error(e)
    }
  }
}
